<script setup lang="ts">
/**
 * Port de `.app-header` (design-reference/styles.css).
 *
 * La grille `46px 1fr 46px` d'origine est conservée telle quelle : la colonne
 * de droite, vide, équilibre la marque de gauche et garde le titre centré.
 */
</script>

<template>
  <header class="app-header" data-testid="header">
    <span class="app-header__flowers" aria-hidden="true"></span>
    <a class="brand" href="#top" aria-label="Sarah et Quentin, retour en haut">
      <img src="/assets/sq-logo-sage.svg" alt="" />
    </a>
    <div class="privacy-title">
      <!-- Deux cœurs qui se chevauchent, comme deux initiales entrelacées, et
           qui battent l'un après l'autre : un seul cœur, deux pulsations. -->
      <span class="privacy-title__hearts" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path
            d="M12 20.4s-6.6-4.1-8.3-8.1C2.3 9.1 4.2 5.9 7.4 5.9c1.8 0 3.2 1 4 2.3l.6.9.6-.9c.8-1.3 2.2-2.3 4-2.3 3.2 0 5.1 3.2 3.7 6.4-1.7 4-8.3 8.1-8.3 8.1Z"
          />
        </svg>
        <svg viewBox="0 0 24 24">
          <path
            d="M12 20.4s-6.6-4.1-8.3-8.1C2.3 9.1 4.2 5.9 7.4 5.9c1.8 0 3.2 1 4 2.3l.6.9.6-.9c.8-1.3 2.2-2.3 4-2.3 3.2 0 5.1 3.2 3.7 6.4-1.7 4-8.3 8.1-8.3 8.1Z"
          />
        </svg>
      </span>
      <span>Sarah &amp; Quentin</span>
    </div>
  </header>
</template>

<style scoped>
.app-header {
  position: relative;
  position: sticky;
  top: 0;
  z-index: 50;
  display: grid;
  grid-template-columns: 46px 1fr 46px;
  align-items: center;
  height: 68px;
  padding: 0 max(16px, env(safe-area-inset-left));
  border-bottom: 1px solid var(--color-line);
  background: rgb(250 248 241 / 0.91);
  backdrop-filter: blur(15px);
  overflow: hidden;
}

/*
 * Guirlande d'aquarelle reprise du fond de l'album, cadrée sur sa bande
 * fleurie du haut. Elle est portée par un calque dédié plutôt que par le fond
 * de l'en-tête : celui-ci doit rester translucide pour que le `backdrop-filter`
 * garde son effet de verre dépoli au défilement.
 */
.app-header__flowers {
  position: absolute;
  inset: 0;
  z-index: -1;
  opacity: 0.5;
  pointer-events: none;
  background:
    linear-gradient(
      90deg,
      rgb(250 248 241 / 0) 0%,
      rgb(250 248 241 / 0.92) 34%,
      rgb(250 248 241 / 0.92) 66%,
      rgb(250 248 241 / 0) 100%
    ),
    url("/assets/watercolor-floral-paper.webp") center 8% / 620px auto no-repeat;
}

.brand {
  display: grid;
  place-items: center;
  width: 38px;
  height: 50px;
  color: var(--color-ink);
  text-decoration: none;
}

.brand img {
  width: auto;
  height: 39px;
}

.privacy-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  text-align: center;
}

.privacy-title__hearts {
  display: flex;
  align-items: center;
}

.privacy-title__hearts svg {
  width: 13px;
  height: 13px;
  fill: var(--color-rose);
}

/* Le second cœur chevauche le premier et bat avec un temps de retard : deux
   battements distincts qui n'en font qu'un, plutôt qu'une pulsation unique. */
.privacy-title__hearts svg:first-child {
  fill: var(--color-rose-light);
  animation: heart-beat 3.2s ease-in-out infinite;
}

.privacy-title__hearts svg:last-child {
  margin-left: -5px;
  animation: heart-beat 3.2s ease-in-out 0.16s infinite;
}

@keyframes heart-beat {
  0%,
  62%,
  100% {
    transform: scale(1);
  }
  68% {
    transform: scale(1.22);
  }
  74% {
    transform: scale(1);
  }
  80% {
    transform: scale(1.14);
  }
  86% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .privacy-title__hearts svg {
    animation: none;
  }
}

/* Sélecteur d'enfant direct impératif : `.privacy-title span` toucherait aussi
   le badge imbriqué et l'emporterait en spécificité sur `.app-header__badge`,
   qui s'étalerait alors sur toute la largeur de l'en-tête. */
.privacy-title > span {
  display: block;
  color: var(--color-muted);
  font-family: var(--font-serif);
  font-size: 16px;
  -webkit-text-stroke: 0.08px currentColor;
}

@media (min-width: 760px) {
  .app-header {
    padding-right: 30px;
    padding-left: 30px;
  }
}
</style>
